import React, { useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Node } from '@tiptap/core';
import './helpdesk-editor.css';

const MIN_SIZE = 80;
const MAX_SIZE = 1200;

const ResizableImage = Image.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      allowBase64: true,
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute('width') ? parseInt(el.getAttribute('width'), 10) : null,
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
      },
      height: {
        default: null,
        parseHTML: (el) => el.getAttribute('height') ? parseInt(el.getAttribute('height'), 10) : null,
        renderHTML: (attrs) => (attrs.height ? { height: attrs.height } : {}),
      },
    };
  },
  addNodeView() {
    return ({ editor, node, getPos }) => {
      const wrapper = document.createElement('span');
      wrapper.className = 'hd-resizable-image-wrap';
      if (node.attrs.textAlign) {
        wrapper.style.display = 'block';
        wrapper.style.textAlign = node.attrs.textAlign;
      }

      const img = document.createElement('img');
      img.src = node.attrs.src;
      img.alt = node.attrs.alt || '';
      img.title = node.attrs.title || '';
      img.draggable = false;
      img.referrerPolicy = 'no-referrer';
      if (node.attrs.width != null) img.style.width = `${node.attrs.width}px`;
      if (node.attrs.height != null) img.style.height = `${node.attrs.height}px`;
      img.style.maxWidth = '100%';
      img.style.display = 'block';

      const handle = document.createElement('span');
      handle.className = 'hd-resize-handle';
      handle.setAttribute('contenteditable', 'false');
      handle.title = 'Drag to resize';

      wrapper.appendChild(img);
      wrapper.appendChild(handle);

      let startX = 0;
      let startY = 0;
      let startW = 0;
      let startH = 0;

      const onMouseMove = (e) => {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        let w = Math.round(startW + dx);
        let h = Math.round(startH + dy);
        w = Math.min(MAX_SIZE, Math.max(MIN_SIZE, w));
        h = Math.min(MAX_SIZE, Math.max(MIN_SIZE, h));
        const pos = typeof getPos === 'function' ? getPos() : undefined;
        if (pos !== undefined && editor.view) {
          const tr = editor.state.tr.setNodeMarkup(pos, null, { ...node.attrs, width: w, height: h });
          editor.view.dispatch(tr);
        }
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        startX = e.clientX;
        startY = e.clientY;
        startW = node.attrs.width ?? img.offsetWidth;
        startH = node.attrs.height ?? img.offsetHeight;
        document.body.style.cursor = 'nwse-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });

      return {
        dom: wrapper,
        update(updatedNode) {
          if (updatedNode.type !== node.type) return false;
          img.src = updatedNode.attrs.src;
          img.alt = updatedNode.attrs.alt || '';
          img.title = updatedNode.attrs.title || '';
          if (updatedNode.attrs.width != null) img.style.width = `${updatedNode.attrs.width}px`;
          else img.style.width = '';
          if (updatedNode.attrs.height != null) img.style.height = `${updatedNode.attrs.height}px`;
          else img.style.height = '';
          if (updatedNode.attrs.textAlign) {
            wrapper.style.display = 'block';
            wrapper.style.textAlign = updatedNode.attrs.textAlign;
          } else {
            wrapper.style.display = '';
            wrapper.style.textAlign = '';
          }
          return true;
        },
      };
    };
  },
});

const Youtube = Node.create({
  name: 'youtube',
  group: 'block',
  atom: true,
  addOptions() {
    return { width: 640, height: 360 };
  },
  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-src') || el.querySelector('iframe')?.getAttribute('src'),
        renderHTML: (attrs) => (attrs.src ? { 'data-src': attrs.src } : {}),
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-youtube-embed], div[class*="youtube"]',
        getAttrs: (dom) => {
          const iframe = dom.querySelector?.('iframe');
          return { src: iframe?.getAttribute('src') || dom.getAttribute('data-src') || null };
        },
      },
      {
        tag: 'iframe[src*="youtube.com"], iframe[src*="youtu.be"]',
        getAttrs: (dom) => ({ src: dom.getAttribute('src') }),
      },
    ];
  },
  renderHTML({ node }) {
    let src = node.attrs.src || '';
    if (src && !src.includes('/embed/')) {
      try {
        if (src.includes('youtube.com/watch?')) {
          const url = new URL(src);
          src = `https://www.youtube.com/embed/${url.searchParams.get('v') || ''}`;
        } else if (src.includes('youtu.be/')) {
          const id = src.split('youtu.be/')[1]?.split('?')[0] || '';
          src = `https://www.youtube.com/embed/${id}`;
        }
      } catch {
        src = node.attrs.src;
      }
    }
    return [
      'div',
      { class: 'hd-youtube-embed', 'data-youtube-embed': '' },
      ['iframe', {
        src,
        width: this.options.width,
        height: this.options.height,
        frameborder: '0',
        allowfullscreen: 'true',
        allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
      }],
    ];
  },
  addCommands() {
    return {
      setYoutubeVideo:
        (url) =>
        ({ commands }) => {
          let src = url;
          if (url.includes('youtube.com/watch?')) {
            try {
              const v = new URL(url).searchParams.get('v');
              src = `https://www.youtube.com/embed/${v}`;
            } catch {}
          } else if (url.includes('youtu.be/')) {
            const id = url.split('youtu.be/')[1]?.split('?')[0] || '';
            src = `https://www.youtube.com/embed/${id}`;
          } else if (url && !url.includes('/embed/')) {
            src = `https://www.youtube.com/embed/${url}`;
          }
          return commands.insertContent({
            type: this.name,
            attrs: { src },
          });
        },
    };
  },
});

export default function HelpDeskEditor({ value, onChange, placeholder }) {
  const imageInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      Underline,
      ResizableImage,
      Youtube,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph', 'image'],
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'hd-editor-prose',
        'data-placeholder': placeholder || 'Write your content... Add images or YouTube videos from the toolbar.',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const html = editor.getHTML();
    const next = value || '';
    if (next !== html) {
      editor.commands.setContent(next || '<p></p>', false);
    }
  }, [value, editor]);

  const addImageFromUrl = useCallback(() => {
    const url = window.prompt('Enter image URL:');
    if (url?.trim()) {
      editor?.chain().focus().setImage({ src: url.trim() }).run();
    }
  }, [editor]);

  const addImageFromFile = useCallback(() => {
    const input = imageInputRef.current;
    if (input) {
      input.value = '';
      input.click();
    }
  }, []);

  const handleImageFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file || !file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        if (dataUrl && editor) {
          editor.chain().focus().setImage({ src: dataUrl }).run();
        }
      };
      reader.readAsDataURL(file);
    },
    [editor]
  );

  const setLink = useCallback(() => {
    const previousUrl = editor?.getAttributes('link').href || '';
    const url = window.prompt('Enter URL:', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addYoutube = useCallback(() => {
    const url = window.prompt('Paste YouTube video URL (e.g. https://www.youtube.com/watch?v=... or https://youtu.be/...):');
    if (url?.trim()) {
      editor?.chain().focus().setYoutubeVideo(url.trim()).run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="hd-rich-editor">
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageFile}
      />
      <div className="hd-rich-toolbar">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'active' : ''}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'active' : ''}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'active' : ''}
          title="Strikethrough"
        >
          S̶
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'active' : ''}
          title="Underline"
        >
          <u>U</u>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={editor.isActive('code') ? 'active' : ''}
          title="Inline code"
        >
          &lt;/&gt;
        </button>
        <span className="hd-toolbar-sep" />
        <span className="hd-toolbar-color-wrap" title="Text color">
          <input
            type="color"
            value={editor.getAttributes('textStyle').color || '#000000'}
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            className="hd-toolbar-color-input"
          />
          <span className="hd-toolbar-color-label">A</span>
        </span>
        <span className="hd-toolbar-sep" />
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'active' : ''}
          title="Align left"
        >
          ⬅
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editor.isActive({ textAlign: 'center' }) ? 'active' : ''}
          title="Align center"
        >
          ↔
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'active' : ''}
          title="Align right"
        >
          ➡
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}
          title="Justify"
        >
          ≡
        </button>
        <span className="hd-toolbar-sep" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'active' : ''}
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive('heading', { level: 3 }) ? 'active' : ''}
          title="Heading 3"
        >
          H3
        </button>
        <span className="hd-toolbar-sep" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'active' : ''}
          title="Bullet list"
        >
          •
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'active' : ''}
          title="Numbered list"
        >
          1.
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'active' : ''}
          title="Blockquote"
        >
          “
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={editor.isActive('codeBlock') ? 'active' : ''}
          title="Code block"
        >
          {'{ }'}
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal rule"
        >
          —
        </button>
        <span className="hd-toolbar-sep" />
        <button type="button" onClick={setLink} className={editor.isActive('link') ? 'active' : ''} title="Link">
          Link
        </button>
        <span className="hd-toolbar-sep" />
        <button type="button" onClick={addImageFromFile} title="Upload image">
          Image
        </button>
        <button type="button" onClick={addImageFromUrl} title="Add image from URL">
          Image URL
        </button>
        <button type="button" onClick={addYoutube} title="Add YouTube video">
          YouTube
        </button>
      </div>
      <div className="hd-rich-content">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
